import type { Asset } from '@compass/client/types/asset';
import type { CallInfo } from '@compass/client/types/call';
import { Clock, Phone, Plus } from 'lucide-react';
import React, { useCallback, useMemo, useRef } from 'react';

import { AudioPlayer, type AudioPlayerHandle } from '@/components/AudioPlayer/AudioPlayer';
import { AudioErrorBoundary } from '@/components/ErrorBoundary/AudioErrorBoundary';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDuration } from '@/utils/date';

interface RecordingCardProps {
  call: CallInfo;
  index: number;
  caseId: string;
  canAttach: boolean;
  isCurrentlyAttached: boolean;
  onAttach: (callId: string) => void;
  audioPlayerRef: React.RefObject<AudioPlayerHandle>;
  onPlay: () => void;
}

/**
 * Individual recording card with audio player and metadata
 */
const RecordingCard: React.FC<RecordingCardProps> = ({
  call,
  index,
  caseId,
  canAttach,
  isCurrentlyAttached,
  onAttach,
  audioPlayerRef,
  onPlay,
}) => {
  // Get the recording asset ID from the call object
  // Each call has its own assets array with specific transcript/recording IDs
  const recordingAssetId = call.assets?.[0]?.recording;

  // Create a minimal asset object for the AudioPlayer
  // AudioPlayer only needs the asset ID to stream the audio via useStreamingAudio
  const audioAssets = useMemo((): Asset[] => {
    if (!recordingAssetId) return [];
    return [{ id: recordingAssetId } as Asset];
  }, [recordingAssetId]);

  const isLoadingAudio = false; // No async fetch needed - we have the ID from the call object

  // Calculate duration from started_at and ended_at
  const duration = useMemo(() => {
    if (!call.started_at || !call.ended_at) return null;
    const start = new Date(call.started_at).getTime();
    const end = new Date(call.ended_at).getTime();
    const durationSeconds = Math.floor((end - start) / 1000);
    return formatDuration(durationSeconds);
  }, [call.started_at, call.ended_at]);

  const handleAttach = useCallback(() => {
    if (call.id) {
      onAttach(call.id);
    }
  }, [call.id, onAttach]);

  const hasAudio = audioAssets.length > 0;

  return (
    <div className="border rounded-lg p-4 border-border">
      {/* Header with recording number and attach button/badge */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-foreground">Recording #{index + 1}</h4>
        {isCurrentlyAttached ? (
          <Badge variant="secondary" className="bg-primary/15 text-primary">
            Attached
          </Badge>
        ) : (
          canAttach && (
            <Button
              variant="default"
              size="sm"
              className="h-6 px-2.5 text-xs"
              onClick={handleAttach}
            >
              <Plus className="w-3 h-3 mr-1" />
              Attach To Case
            </Button>
          )
        )}
      </div>

      {/* Metadata grid */}
      {/* Metadata */}
      {(duration || call.phone) && (
        <div className="flex items-center gap-4 mb-4 text-sm">
          {duration && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{duration}</span>
            </div>
          )}
          {call.phone && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="w-4 h-4" />
              <span>{call.phone}</span>
            </div>
          )}
        </div>
      )}

      {/* Audio Player - show when loading or has audio */}
      {(isLoadingAudio || hasAudio) && call.id ? (
        <div className="border rounded-md overflow-hidden">
          <AudioErrorBoundary>
            <AudioPlayer
              ref={audioPlayerRef}
              caseRecordId={caseId}
              callId={call.id}
              callDetails={{ sid: call.sid }}
              assets={audioAssets}
              onPlay={onPlay}
            />
          </AudioErrorBoundary>
        </div>
      ) : (
        <div className="border rounded-md p-4 text-center text-muted-foreground text-sm">
          Audio recording not available
        </div>
      )}
    </div>
  );
};

interface RecordingSelectionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** List of calls/recordings */
  calls: CallInfo[];
  /** Case record ID */
  caseId: string;
  /** Whether the user can attach recordings (only in intake_draft/review_needed) */
  canAttach: boolean;
  /** Callback when a recording is attached */
  onAttach: (callId: string) => void;
  /** ID of the currently attached call (to highlight) */
  currentlyAttachedCallId?: string;
}

/**
 * Modal for selecting and attaching recordings to a case.
 * Shows a list of available recordings with audio players and attach buttons.
 * Only one recording can play at a time.
 */
export const RecordingSelectionModal: React.FC<RecordingSelectionModalProps> = ({
  isOpen,
  onClose,
  calls,
  caseId,
  canAttach,
  onAttach,
  currentlyAttachedCallId,
}) => {
  // Create refs for all audio players
  const audioPlayerRefs = useRef<Map<string, React.RefObject<AudioPlayerHandle>>>(new Map());

  // Get or create a ref for a specific call
  const getAudioPlayerRef = useCallback((callId: string) => {
    if (!audioPlayerRefs.current.has(callId)) {
      audioPlayerRefs.current.set(callId, React.createRef<AudioPlayerHandle>());
    }
    return audioPlayerRefs.current.get(callId)!;
  }, []);

  // Handle when a recording starts playing - stop all others
  const handleRecordingPlay = useCallback((callId: string) => {
    // Stop all other audio players
    audioPlayerRefs.current.forEach((ref, id) => {
      if (id !== callId && ref.current) {
        ref.current.stop();
      }
    });
  }, []);

  const handleAttach = useCallback(
    (callId: string) => {
      // Stop any playing audio before attaching
      audioPlayerRefs.current.forEach(ref => {
        if (ref.current) {
          ref.current.stop();
        }
      });
      // Parent handles closing the modal after action completes
      onAttach(callId);
    },
    [onAttach],
  );

  // Stop all audio when modal closes
  const handleClose = useCallback(() => {
    audioPlayerRefs.current.forEach(ref => {
      if (ref.current) {
        ref.current.stop();
      }
    });
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Available Recordings</DialogTitle>
          <DialogDescription>
            Select the correct recording to attach to this case.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 space-y-4">
          {calls
            .filter((call): call is CallInfo & { id: string } => !!call.id)
            .sort((a, b) => a.id.localeCompare(b.id))
            .map((call, index) => (
              <RecordingCard
                key={call.id}
                call={call}
                index={index}
                caseId={caseId}
                canAttach={canAttach}
                isCurrentlyAttached={call.id === currentlyAttachedCallId}
                onAttach={handleAttach}
                audioPlayerRef={getAudioPlayerRef(call.id)}
                onPlay={() => handleRecordingPlay(call.id)}
              />
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
